import React, { useState } from "react";

import {
    Box,
    Paper,
    TextField,
    Grid,
    styled,
    Card,
    Divider,
    IconButton,
    Button,
    Typography,
    InputAdornment,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox
} from '@mui/material';

import dayjs from "dayjs";

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt"
import { useAppContextFeatures } from "../../../context/AppContext";

import { useRepositories, repositoryTypes, targetTypes } from '../../../hooks/api/useRepositories';
import { initialValues, validationSchema, initialRowValues } from "../../../schema/repositories/containerSchema";
import DatePickerComponent from "../../common/DatePickerComponent"
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import { CommonIcons, icons } from "../../common/CommonIcons";
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useAuthAccess } from "../../../hooks/useAuthAccess"
import { useTranslations } from '../../../hooks/useTranslations';

import SearchItems from "../../dialog/SearchItems"

// import { initialValues, validationSchema, initialRowValues } from "../../../schema/repositories/containerSchema"

import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes"
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";

import LocationSelector from "../../selector/LocationSelector"

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

export default function NewContainer({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@repository"] })

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useRepositories({ repositoryType: repositoryTypes.container, targetType: targetTypes.container });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.repository.id, scopeTypes.repository.container.id)

    const { getAvailableLocations } = useAuthAccess();
    const availableLocations = getAvailableLocations();

    const { setSystemLoading, getLocationIds, setLocationIds } = useAppContextFeatures();
    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [allowItems, setAllowItems] = useState(false)

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try {

            let dataItems = {}

            if (allowItems === true) {
                let items = currentValues.rows.map((i) => {
                    return {
                        id: i?.itemId,
                        code: i?.code,
                        name: i?.itemName,
                        qty: i?.qty,
                        uom_lbl: i?.uomLabel,
                        uom: i?.uomId,
                        price: i?.price,
                        description: i?.description,
                        remark: i?.itemRemark
                    }
                })

                dataItems = {
                    items
                }
            }

            let data = {
                repository: {
                    repo_number: currentValues?.repoNumber,
                    number_alternative: currentValues?.repoAlternativeNumber,
                    repo_date: dayjs(currentValues?.initialDate).format("YYYY-MM-DD"),
                    remark: currentValues?.remark,
                },
                props: {
                    location_id: currentValues?.locationId,
                    to_location_id: currentValues?.toLocationId,
                    number_seal: currentValues?.numberSeal,
                    number_marking: currentValues?.repoMarking,
                    load_date: dayjs(currentValues?.loadDate).format("YYYY-MM-DD"),
                    eta_date: "",
                    supervisor: currentValues?.supervisor,
                    shipper: currentValues?.shipper,
                    customAgent: currentValues?.customAgent,
                },
                ...dataItems
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("repositories/containers"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

        setSystemLoading(false)
    }

    const validateLocationSelection = () => {
        const selected = getLocationIds()
        return selected && selected.length === 1
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue, handleSubmit } = formik;

    const handleRemoveItem = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, t(`itemsAtLeastHave1Row`))
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const handleAddItem = ({ rows, setFieldValue }) => {

        rows.push(initialRowValues);
        setFieldValue('rows', rows)

    }

    const RenderContainer = () => {

        return (
            <StyledCard sx={{ mt: 1 }} >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('profileInformation')}
                            subTitle={t('provideInformationAboutContainer')}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={8}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    autoFocus
                                    fullWidth
                                    name={"repoNumber"}
                                    label={t("repositoryNo")}
                                    type="text"
                                    variant="outlined"
                                    value={values.repoNumber}
                                    onChange={handleChange}
                                    error={touched.repoNumber && Boolean(errors.repoNumber)}
                                    helperText={touched.repoNumber && errors.repoNumber}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <></>
                            </Grid> */}
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoAlternativeNumber"}
                                    label={t("secondaryNo")}
                                    type="test"
                                    variant="outlined"
                                    value={values.repoAlternativeNumber}
                                    onChange={handleChange}
                                    error={touched.repoAlternativeNumber && Boolean(errors.repoAlternativeNumber)}
                                    helperText={touched.repoAlternativeNumber && errors.repoAlternativeNumber}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"numberSeal"}
                                    label={t("sealNo")}
                                    type="text"
                                    variant="outlined"
                                    value={values.numberSeal}
                                    onChange={handleChange}
                                    error={touched.numberSeal && Boolean(errors.numberSeal)}
                                    helperText={touched.numberSeal && errors.numberSeal}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoMarking"}
                                    label={t("repositoryMarking")}
                                    type="text"
                                    variant="outlined"
                                    value={values.repoMarking}
                                    onChange={handleChange}
                                    error={touched.repoMarking && Boolean(errors.repoMarking)}
                                    helperText={touched.repoMarking && errors.repoMarking}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "initialDate",
                                        label: t("initialDate")
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "loadDate",
                                        label: t("loadDate")
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"supervisor"}
                                    label={t("supervisor")}
                                    type="text"
                                    variant="outlined"
                                    value={values.supervisor}
                                    onChange={handleChange}
                                    error={touched.supervisor && Boolean(errors.supervisor)}
                                    helperText={touched.supervisor && errors.supervisor}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"shipper"}
                                    label={t("shipper")}
                                    type="text"
                                    variant="outlined"
                                    value={values.shipper}
                                    onChange={handleChange}
                                    error={touched.shipper && Boolean(errors.shipper)}
                                    helperText={touched.shipper && errors.shipper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"customAgent"}
                                    label={t("customAgent")}
                                    type="text"
                                    variant="outlined"
                                    value={values.customAgent}
                                    onChange={handleChange}
                                    error={touched.customAgent && Boolean(errors.customAgent)}
                                    helperText={touched.customAgent && errors.customAgent}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t("remark")}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={"3"}
                                    value={values.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>

        )
    }

    const RenderSummaryRow = ({ values, errors, touched, handleChange, setFieldValue }) => {
        return (
            <Grid item xs={12} sm={12} md={12}>

                <Grid container spacing={1}>

                    <Grid item xs={12} sm={12} md={4}>

                        <Button
                            variant="outlined"
                            sx={{ mt: 2 }}
                            fullWidth
                            startIcon={<CommonIcons name={icons.add} />}
                            onClick={() => handleAddItem({ rows: values.rows, setFieldValue })}
                        >
                            {t('addItem')}
                        </Button>

                    </Grid>

                </Grid>

                {/* <Grid container spacing={1}>

                    <Grid item xs={12} sm={12} md={9}>
                        <></>
                    </Grid>

                    <Grid item xs={12} sm={12} md={3}>
                        <TextField
                            inputProps={{
                                style: {
                                    textAlign: "right"
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography
                                            sx={{
                                                fontSize: '1.5rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            =
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            margin="dense"
                            size="small"
                            disabled
                            fullWidth
                            name={"totalamount"}
                            label="Total Quantity"
                            type="text"                                         
                            variant="outlined"
                            value={values?.totalamount? values.totalamount : "0.00"}
                        />
                    </Grid>

                </Grid> */}

            </Grid>
        )
    }

    const RenderRows = () => {

        const rows = values?.rows;

        return (
            <Box
            >
                <Box
                >
                    {
                        rows && rows.length > 0
                        && rows.map((item, index) => {

                            const itemValues = item;
                            let itemTouched = touched?.rows
                            let itemErrors = errors?.rows

                            if (itemTouched) {
                                itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                            }

                            if (itemErrors) {
                                itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                            }

                            return (
                                <Grid key={index} item xs={12} sm={12} md={12}>

                                    <Grid container spacing={1} >

                                        <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleRemoveItem({ rows: values.rows, index, setFieldValue })}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3}>

                                            <SearchItems
                                                id={`rows.${index}.name`}
                                                handleOnCancel={() => { }}
                                                handleOnSearchChange={(event) => {
                                                    setFieldValue(`rows.${index}.name`, `${event.target.value}`)
                                                }}
                                                handleOnSelect={({ id, item }) => {
                                                    console.log('id', id, item)
                                                    setFieldValue(`rows.${index}.itemId`, item?.id)
                                                    setFieldValue(`rows.${index}.itemName`, item?.item_name)
                                                    setFieldValue(`rows.${index}.description`, item?.description)
                                                    setFieldValue(`rows.${index}.code`, item?.code)
                                                    setFieldValue(`rows.${index}.qty`, 1)
                                                    setFieldValue(`rows.${index}.itemRemark`, item?.remark)
                                                    setFieldValue(`rows.${index}.uomLabel`, item?.uom_name)
                                                    setFieldValue(`rows.${index}.uomId`, item?.uom_id)
                                                    setFieldValue(`rows.${index}.price`, item?.uom_price)
                                                }}
                                                textProps={{
                                                    disabled: true,
                                                    value: itemValues?.itemName ? itemValues.itemName : "",
                                                    error: Boolean(itemErrors?.itemName),
                                                    helperText: itemErrors?.itemName
                                                }}
                                            />

                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1.5}>
                                            <TextField
                                                disabled
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.code`}
                                                label={t("code")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.code ? itemValues.code : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.code)}
                                                helperText={itemErrors?.code}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.description`}
                                                label={t("description")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.description ? itemValues.description : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.description)}
                                                helperText={itemErrors?.description}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.itemRemark`}
                                                label={t("remark")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.itemRemark ? itemValues.itemRemark : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.itemRemark)}
                                                helperText={itemErrors?.itemRemark}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <TextField
                                                inputProps={{
                                                    style: {
                                                        textAlign: "center"
                                                    }
                                                }}
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.qty`}
                                                label={t("qty")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.qty ? itemValues.qty : ""}
                                                onChange={(event) => {
                                                    setFieldValue(`rows.${index}.qty`, event.target.value);
                                                    // handleDisplayRowChange({ index, qty: event.target.value});
                                                }}
                                                error={Boolean(itemErrors?.qty)}
                                                helperText={itemErrors?.qty}
                                            />
                                        </Grid>

                                    </Grid>
                                </Grid>
                            )
                        })
                    }
                </Box>

                <Grid item xs={12}>
                    <Divider sx={{ mt: 1, mb: 0 }} />
                </Grid>

                {
                    RenderSummaryRow({ values, errors, touched, handleChange, setFieldValue })
                }

            </Box>
        )
    }

    const RenderFormLocation = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t(`location`)}
                            subTitle={t(`relatedWarehouseLocationToProcess`)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={8}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    disabled
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"locationId"}
                                    label={t("location")}
                                    type="text"
                                    variant="outlined"
                                    value={values.locationName}
                                    error={touched.locationId && Boolean(errors.locationId)}
                                    helperText={touched.locationId && errors.locationId}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    color="primary"
                                                    sx={{
                                                        my: 1
                                                    }}
                                                    onClick={() => {
                                                        setLocationIds([])
                                                    }}
                                                >
                                                    <StyledIcon
                                                        sx={{
                                                            width: 25,
                                                            height: 25,
                                                            m: 0,
                                                            p: 0
                                                        }}
                                                    >
                                                        <CommonIcons name={icons.edit} />
                                                    </StyledIcon>
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.toLocationId)}
                                >
                                    <InputLabel id="toLocationId">{t('destinationLocation')}</InputLabel>
                                    <Select
                                        label={t('destinationLocation')}
                                        size="small"
                                        name={`toLocationId`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={(event) => {

                                            let locId = event.target.value;

                                            // let locationSelected = availableLocations.filter(i => i.id === locId)

                                            setFieldValue(
                                                `toLocationId`,
                                                locId
                                            )
                                        }}
                                        value={values?.toLocationId ? values?.toLocationId : ""}
                                    >
                                        {
                                            availableLocations?.map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={i.id}>
                                                        {i.location_name}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.toLocationId}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        return (
            <form onSubmit={handleSubmit}>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12}>

                        {
                            RenderFormLocation()
                        }

                    </Grid>
                </Grid>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        {
                            RenderContainer()
                        }

                    </Grid>
                </Grid>

                <StyledCard sx={{ mt: 1 }}>
                    <Grid container rowSpacing={0}>
                        <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t(`items`)}
                                subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={6} textAlign={"right"}>

                            <Checkbox
                                checked={
                                    Boolean(allowItems)
                                }
                                // value={Boolean(true)}
                                onChange={(e) => {
                                    let val = e.target.checked
                                    setAllowItems(val)
                                }}
                            />{t('assignItems')}

                        </Grid>

                        {
                            allowItems &&
                            <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                                {
                                    RenderRows()
                                }

                            </Grid>
                        }


                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>

            </form>
        )
    }

    const RenderAll = () => {

        return validateLocationSelection() ?
            (
                RenderForm()
            )
            :
            (
                <LocationSelector
                    config={{
                        title: t(`selectOnly1Location`),
                        description: t(`youHaveToSelectAnyoneOfTheWarehouseLocationToProceed`),
                    }}
                    maxSelect={1}
                    handleOnNext={({ selected }) => {

                        // console.log('selected', selected)

                        setLocationIds([selected[0].id])
                        setFieldValue(`locationId`, selected[0].id)
                        setFieldValue(`locationName`, selected[0].location_name)

                    }}
                />
            )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newContainer })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("newContainer")}
                breadcrumbs={[
                    {
                        displayText: t("containers"),
                        props: {
                            href: getRoute(`repositories/containers`)
                        }
                    },
                    {
                        displayText: t("newContainer"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {/* {JSON.stringify(errors)}
            {JSON.stringify(values)} */}

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderAll()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}